import { useEffect, useState, useCallback } from 'react';
import {
  Package, Loader2, AlertCircle, X, Plus, Pencil, Trash2,
} from 'lucide-react';
import {
  listRechargeKits, createRechargeKit, updateRechargeKit, deleteRechargeKit,
  type RechargeKit, type RechargeKitInput,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';

const kitCols = (
  onEdit: (k: RechargeKit) => void,
  onDelete: (id: number) => void,
): DataTableColumn<RechargeKit>[] => [
  { key: 'id', label: 'ID', width: '60px',
    cell: (k) => <span className="text-xs font-mono text-white/55">{k.id}</span> },
  { key: 'title', label: 'Título', width: '2fr',
    cell: (k) => <span className="text-sm font-bold text-white truncate">{k.title || '—'}</span> },
  { key: 'cash', label: 'Cash (eCoin)', width: '120px',
    cell: (k) => <span className="text-sm font-bold text-[#00e1d7]">{(k.cash ?? 0).toLocaleString('pt-BR')}</span> },
  { key: 'bonus', label: 'Bônus', width: '110px',
    cell: (k) => (k.bonus ?? 0) > 0
      ? <span className="text-sm font-bold text-[#00e1d7]">+{(k.bonus ?? 0).toLocaleString('pt-BR')}</span>
      : <span className="text-sm text-white/35">—</span> },
  { key: 'brl', label: 'BRL', width: '120px',
    cell: (k) => <span className="text-sm text-white/85">R$ {k.brl?.toFixed(2) ?? '—'}</span> },
  { key: 'usd', label: 'USD', width: '120px',
    cell: (k) => <span className="text-sm text-white/55">$ {k.usd?.toFixed(2) ?? '—'}</span> },
  { key: 'active', label: 'Ativo', width: '80px',
    cell: (k) => k.active === 1
      ? <span className="text-[10px] font-bold text-emerald-400">SIM</span>
      : <span className="text-[10px] font-bold text-white/35">NÃO</span> },
  { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
    cell: (k) => (
      <RowActions
        actions={[
          { label: 'Editar', icon: <Pencil size={13} />, onClick: () => onEdit(k), variant: 'warning' },
          { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => onDelete(k.id), variant: 'danger' },
        ]}
      />
    ) },
];

export default function RechargesPage() {
  const { notify } = useToast();
  const [items, setItems] = useState<RechargeKit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RechargeKit | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setItems(await listRechargeKits()); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id: number) => {
    if (!confirm('Deletar esse kit?')) return;
    try {
      await deleteRechargeKit(id);
      notify('success', 'Kit deletado');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <Package className="text-[#00e1d7]" size={26} />
          Recargas
        </h1>
        <p className="text-sm text-white/65 mt-1">Kits / pacotes de recarga — as vendas ficam no menu Vendas.</p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-white/65">{items.length} kit(s) · CabalCash.web_recharges_kit</p>
        <button onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                     bg-[#00e1d7] hover:bg-[#00c9c0] text-[#0d141b] transition cursor-pointer">
          <Plus size={14} /> Novo Kit
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<RechargeKit>
        columns={kitCols(setEditing, handleDelete)}
        rows={items}
        rowKey={(k) => k.id}
        loading={loading}
        empty="Nenhum kit cadastrado"
        mobileTitle={(k) => (
          <span className="flex items-center gap-2">{k.title || '—'} <span className="text-[10px] text-white/45 font-mono">#{k.id}</span></span>
        )}
        mobileSubtitle={(k) => `${(k.cash ?? 0).toLocaleString('pt-BR')} Cash · R$ ${k.brl?.toFixed(2) ?? '—'}`}
        mobileActions={(k) => (
          <RowActions
            actions={[
              { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditing(k), variant: 'warning' },
              { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDelete(k.id), variant: 'danger' },
            ]}
            align="left"
          />
        )}
      />

      {(editing || creating) && (
        <KitEditor kit={editing} onClose={() => { setEditing(null); setCreating(false); }}
                   onSaved={async () => { await fetchData(); setEditing(null); setCreating(false); }} />
      )}
    </div>
  );
}

function KitEditor({ kit, onClose, onSaved }: { kit: RechargeKit | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { notify } = useToast();
  const [title, setTitle] = useState(kit?.title || '');
  const [cash, setCash] = useState(String(kit?.cash ?? 0));
  const [bonus, setBonus] = useState(kit?.bonus ? String(kit.bonus) : '');
  const [brl, setBrl] = useState(String(kit?.brl ?? ''));
  const [usd, setUsd] = useState(String(kit?.usd ?? ''));
  const [image, setImage] = useState(kit?.image || '');
  const [active, setActive] = useState(kit?.active ?? 1);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload: RechargeKitInput = {
        title: title || undefined,
        cash: parseInt(cash) || 0,
        bonus: parseInt(bonus) || 0,
        brl: brl ? parseFloat(brl) : undefined,
        usd: usd ? parseFloat(usd) : undefined,
        image: image || undefined,
        active: active ? 1 : 0,
      };
      if (kit) await updateRechargeKit(kit.id, payload);
      else await createRechargeKit(payload);
      notify('success', kit ? 'Kit atualizado' : 'Kit criado');
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">{kit ? 'Editar' : 'Novo'} Kit de Recarga</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Título" value={title} onChange={setTitle} placeholder="ex: 10.000 eCoins" />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Cash (eCoins)" value={cash} onChange={setCash} numeric />
            <Field label="Preço BRL" value={brl} onChange={setBrl} numeric />
            <Field label="Preço USD" value={usd} onChange={setUsd} numeric />
          </div>
          <div>
            <Field label="Bônus (Cash extra — opcional)" value={bonus} onChange={setBonus} numeric placeholder="0" />
            <p className="text-[10px] text-white/40 mt-1">
              Valor inteiro de Cash creditado ALÉM do Cash do kit quando a recarga é paga. Deixe vazio ou 0 pra kit sem bônus.
            </p>
          </div>
          <Field label="Imagem (URL)" value={image} onChange={setImage} mono />
          <label className="flex items-center gap-2 text-sm text-white/85">
            <input type="checkbox" checked={!!active} onChange={(e) => setActive(e.target.checked ? 1 : 0)} />
            Kit ativo (visível pra players)
          </label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
            Cancelar
          </button>
          <button onClick={save} disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg bg-[#00e1d7] hover:bg-[#00c9c0] text-[#0d141b]
                       font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, numeric, mono, placeholder }:
  { label: string; value: string; onChange: (v: string) => void; numeric?: boolean; mono?: boolean; placeholder?: string }) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <input
        type="text" inputMode={numeric ? 'numeric' : undefined}
        value={value} placeholder={placeholder}
        onChange={(e) => onChange(numeric ? e.target.value.replace(/[^\d.]/g, '') : e.target.value)}
        className={`w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}
